/** The tooltip state every dashboard-band chart shares: what is shown, and where.
 *
 * `show` anchors to an element inside the wrapper (top centre), so hover and keyboard focus produce
 * the same tooltip. `hide` clears it. The tooltip itself is `ChartTooltip`, `aria-hidden`: the same
 * words are the mark's accessible name, so nothing is lost to a listener.
 */

import { useCallback, useState } from "react";
import type { RefObject } from "react";

import { anchorWithin } from "./tipAnchor";
import type { TipAnchor } from "./tipAnchor";

export interface Tip {
  anchor: TipAnchor;
  title: string;
  lines: readonly string[];
}

export function useChartTip(wrap: RefObject<HTMLElement | null>) {
  const [tip, setTip] = useState<Tip | null>(null);
  const show = useCallback(
    (element: Element | null | undefined, title: string, lines: readonly string[]) => {
      if (!element || !wrap.current) return;
      setTip({ anchor: anchorWithin(wrap.current, element), title, lines });
    },
    [wrap],
  );
  const hide = useCallback(() => {
    setTip(null);
  }, []);
  return { tip, show, hide };
}
